import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import {
  Button,
  Checkbox,
  IconButton,
  InputLabel,
  Stack,
  TextField,
  Tooltip,
} from "@mui/material";
import { ReactComponent as HelpIcon } from "images/help-icon.svg";
import { customColors } from "models/customColors";
import { birthDateValidation, phoneNumberValidation } from "models/validations";
import { createRef, useState } from "react";
import { DialogWindow } from "UI/DialogWindow";
import { FormDateSelector } from "UI/FormDateSelector";
import { FormMaskedTextField } from "UI/FormMaskedTextField";
import { FormTextField } from "UI/FormTextField";
import { ButtonInfo, Error } from "./ReferralForm.common";
import { FormSelectField } from "UI/FormSelectField";

const UPLOAD_TOTAL_FILES_SIZE_LIMIT_IN_BYTES = 100 * 1024 * 1024;

export const PatientDetailsForm = ({
  handleResetAndBack,
  recipientName,
  provider,
  sender,
  control,
  watch,
  setValue,
  getValues,
  trigger,
  reset,
  isButtonEnabled,
  loading,
  setLoading,
  prevValues,
  onQuestionSubmit,
  handleClose = null,
  originator,
}) => {
  const [fileError, setFileError] = useState(null);
  const [urgencyError, setUrgencyError] = useState(null);
  const [genderError, setGenderError] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadedFileIds, setUploadedFileIds] = useState([]);
  const fileRef = createRef();

  const setGender = (value) => {
    setValue("gender", value);
    setGenderError(null);
  };

  const setUrgency = (value) => {
    setValue("urgency", value);
    setUrgencyError(null);
  };

  const clearForm = () => {
    reset(prevValues);
    setUrgencyError(null);
    setGenderError(null);
    if (fileRef.current) {
      fileRef.current.value = "";
    }
    setSelectedFiles([]);
    setUploadedFileIds([]);
    setFileError(null);
  };

  const checkIfSizeIsTooLarge = (files) => {
    const totalSize = files.reduce((acc, file) => acc + file.size, 0);
    if (totalSize > UPLOAD_TOTAL_FILES_SIZE_LIMIT_IN_BYTES) {
      return true;
    }
    return false;
  };

  const onReferralSubmit = async () => {
    const values = getValues();
    let withErrors = false;

    if (!values?.gender) {
      setGenderError("is required");
      withErrors = true;
    }
    if (!values?.urgency) {
      setUrgencyError("is required");
      withErrors = true;
    }
    const valid = await trigger();
    if (!valid) {
      withErrors = true;
    }
    if (withErrors) return;

    if (checkIfSizeIsTooLarge(selectedFiles)) {
      setFileError("Your total files size is larger than 100MB.");
      return;
    }

    onQuestionSubmit({ ...values, uploadedFileIds });
  };

  const handleFileChange = async (event) => {
    const files = Array.from(event.target.files);

    if (!files.length) return;

    if (checkIfSizeIsTooLarge(files)) {
      setFileError(
        "Your total files size is larger than 100MB. Try to upload them one by one."
      );
      if (fileRef.current) {
        fileRef.current.value = "";
      }
      event.target.value = "";
      return;
    }

    setLoading(true);
    setFileError(null);

    try {
      const formData = new FormData();
      formData.append("dashboardAccessToken", originator?.dashboardAccessToken);

      for (const newFile of files) {
        formData.append("files[]", newFile);
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_V2_URL}/referral/registration/files/upload`,
        {
          method: "POST",
          body: formData,
        }
      );
      const result = await response.json();

      if (response?.status !== 200 || result?.error) {
        setFileError(
          result?.message ?? result?.error ?? "Something went wrong"
        );
      } else {
        setSelectedFiles((prev) => [...prev, ...files]);
        setUploadedFileIds((prev) => [...prev, ...result]);
      }
    } catch (error) {
      setFileError(error?.message ?? error?.error ?? "Something went wrong");
    } finally {
      setLoading(false);
      event.target.value = "";
    }
  };

  return (
    <DialogWindow
      open
      handleClose={handleClose}
      withCloseButton={!!handleClose}
      title={
        <Stack direction={"row"} alignItems={"center"} gap={1}>
          <IconButton aria-label="close" onClick={handleResetAndBack}>
            <ArrowBackIosNewIcon />
          </IconButton>
          Digital Referral Form
        </Stack>
      }
      content={
        <Stack alignItems={"stretch"} gap={1}>
          <ButtonInfo
            recipientName={recipientName}
            provider={provider}
            sender={sender}
          />
          <Stack
            direction={"row"}
            flexWrap={"wrap"}
            alignItems={"flex-start"}
            gap={4}
          >
            <Stack flex={1} gap={1}>
              <Stack alignItems={"flex-start"}>
                <InputLabel htmlFor="speciality">Patient full name</InputLabel>
                <FormTextField
                  id="patientName"
                  name="patientName"
                  control={control}
                  InputProps={{
                    autoComplete: "off",
                  }}
                  rules={{ required: "is required" }}
                />
              </Stack>
              <Stack alignItems={"flex-start"}>
                <InputLabel htmlFor="patientDob">Date of birth</InputLabel>
                <FormDateSelector
                  id="patientDob"
                  name="patientDob"
                  control={control}
                  size="small"
                  rules={birthDateValidation}
                  maxDate={new Date()}
                />
              </Stack>
              <Stack alignItems={"flex-start"}>
                <InputLabel>Patient gender:</InputLabel>
                <Stack direction={"row"} gap={2}>
                  <InputLabel htmlFor="male" sx={{ cursor: "pointer" }}>
                    <Stack direction={"row"} alignItems={"center"}>
                      <Checkbox
                        id="male"
                        checked={watch("gender") === "Male"}
                        onChange={() => setGender("Male")}
                      />
                      Male
                    </Stack>
                  </InputLabel>
                  <InputLabel htmlFor="female" sx={{ cursor: "pointer" }}>
                    <Stack direction={"row"} alignItems={"center"}>
                      <Checkbox
                        id="female"
                        checked={watch("gender") === "Female"}
                        onChange={() => setGender("Female")}
                      />
                      Female
                    </Stack>
                  </InputLabel>
                </Stack>
                {genderError && <Error>{genderError}</Error>}
              </Stack>
              <Stack alignItems={"flex-start"}>
                <Stack direction={"row"} gap={0.5}>
                  <InputLabel htmlFor="state">
                    Attach patient records (optional)
                  </InputLabel>
                  <Tooltip title="Demographics, Clinical Notes, Imaging Results, Recent Labs, Copy of Insurance & ID, etc.">
                    <HelpIcon width={18} />
                  </Tooltip>
                </Stack>
                <TextField
                  type="file"
                  size="small"
                  error={!!fileError}
                  // @ts-ignore
                  helperText={fileError}
                  inputRef={fileRef}
                  inputProps={{
                    multiple: true,
                    style: {
                      color: "gray",
                    },
                  }}
                  sx={{ width: "100%" }}
                  onChange={handleFileChange}
                />
                <Stack mt={1}>
                  {selectedFiles?.map((file) => (
                    <Stack
                      key={file.name}
                      direction={"row"}
                      justifyContent={"space-between"}
                      gap={1}
                      rowGap={0.5}
                      sx={{ fontSize: 12, color: customColors.mainColor }}
                    >
                      <div className="flex-1 break-all text-left">
                        {file.name}
                      </div>
                      <div className="text-gray-400">
                        {(file.size / 1024).toFixed(2)} KB
                      </div>
                    </Stack>
                  ))}
                </Stack>
              </Stack>
            </Stack>
            <Stack flex={1} gap={1}>
              <Stack alignItems={"flex-start"}>
                <InputLabel htmlFor="patientPhone">Patient Phone</InputLabel>
                <FormMaskedTextField
                  id="patientPhone"
                  name="patientPhone"
                  control={control}
                  rules={phoneNumberValidation}
                  mask="999-999-9999"
                  InputProps={{
                    autoComplete: "off",
                  }}
                />
              </Stack>
              <Stack alignItems={"flex-start"}>
                <InputLabel htmlFor="location">
                  Where is the patient?
                </InputLabel>
                <FormSelectField
                  id="location"
                  name="location"
                  control={control}
                  rules={{ required: "is required" }}
                  options={{
                    Home: "Home",
                    Facility: "Facility",
                  }}
                />
              </Stack>
              <Stack alignItems={"flex-start"}>
                <InputLabel>Urgency:</InputLabel>
                <Stack direction={"row"} gap={2}>
                  <InputLabel htmlFor="stat" sx={{ cursor: "pointer" }}>
                    <Stack direction={"row"} alignItems={"center"}>
                      <Checkbox
                        id="stat"
                        checked={watch("urgency") === "STAT"}
                        onChange={() => setUrgency("STAT")}
                      />
                      STAT
                    </Stack>
                  </InputLabel>
                  <InputLabel htmlFor="routine" sx={{ cursor: "pointer" }}>
                    <Stack direction={"row"} alignItems={"center"}>
                      <Checkbox
                        id="routine"
                        checked={watch("urgency") === "Routine"}
                        onChange={() => setUrgency("Routine")}
                      />
                      Routine
                    </Stack>
                  </InputLabel>
                </Stack>
                {urgencyError && <Error>{urgencyError}</Error>}
              </Stack>
              <Stack alignItems={"flex-start"}>
                <InputLabel htmlFor="reason">
                  Reason for referral (optional)
                </InputLabel>
                <FormTextField
                  id="reason"
                  name="reason"
                  control={control}
                  InputProps={{
                    autoComplete: "off",
                  }}
                  multiline
                />
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      }
      actions={
        <Stack
          flex={1}
          direction={"row"}
          gap={1}
          justifyContent={"center"}
          alignItems={"center"}
        >
          <Button
            variant="outlined"
            color="primary"
            disabled={!isButtonEnabled || loading}
            onClick={clearForm}
            sx={{ padding: "8px 24px" }}
          >
            Clear
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={!isButtonEnabled || loading}
            onClick={onReferralSubmit}
            sx={{ padding: "8px 24px" }}
          >
            Send Referral
          </Button>
        </Stack>
      }
    />
  );
};
